import { useCallback } from 'react';
import { bugtracker } from '@99designs/design-services-common';
import { useDesignLiveAppointmentUpdatedMutation } from '../../../../../graph';
import { buildDesignLiveAppointmentInput, parseAppointmentValue } from './appointmentValue';
import { useUpdateDateField } from './useUpdateDateField';

export function useUpdateDesignLiveAppointment(
    key: string,
    briefId?: string,
    initialTime?: string
) {
    const updateField = useUpdateDateField(key, briefId, initialTime);
    const [designLiveAppointmentUpdated] = useDesignLiveAppointmentUpdatedMutation();

    return useCallback(
        async (value: string) => {
            const result = await updateField(value);
            const briefData = result?.data;
            if (!briefData?.setField) {
                return;
            }

            const correlationId = briefData.setField.correlationId;
            if (!correlationId) {
                return;
            }

            const field = briefData.setField.dynamicFields.find((f) => f.id === key);
            if (!field) {
                return;
            }
            if (field.__typename !== 'ClientConfiguredField') {
                throw new Error(
                    "Field is not a ClientConfiguredField. This schema is not compatible with the fields currently in use on the front end. And will cause all kinds of trouble. If you're seeing this error in bugsnag, investigate immediately"
                );
            }

            const { time, timezone, calendarId } = parseAppointmentValue(value);
            if (!time || !timezone) {
                return;
            }

            try {
                const { errors } = await designLiveAppointmentUpdated({
                    variables: {
                        requestId: correlationId,
                        input: buildDesignLiveAppointmentInput(time, timezone, calendarId),
                    },
                });

                if (errors && errors.length > 0) {
                    throw new Error(errors.map((e) => e.message).join('; '));
                }
            } catch (e) {
                const err = e instanceof Error ? e : new Error(String(e));
                bugtracker.notify(err, (event) => {
                    event.context = `Error firing DesignLiveAppointmentUpdated for requestId: ${correlationId}`;
                    event.addMetadata('appointment', {
                        requestId: correlationId,
                        time,
                        timezone,
                        calendarId,
                    });
                });
            }
        },
        [key, updateField, designLiveAppointmentUpdated]
    );
}
